import posthog from 'posthog-js';
import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { magic } from '../lib/magic';
import { getGasSponsorshipInfo } from '../lib/onchain';
import { walletService } from '../services/walletService';
import { defaultLogger } from '../utils/logger';
import { API_URL } from '../config';

interface User {
  issuer: string;
  email: string;
  publicAddress: string;
  walletAddress: string; // This will be the smart_contract_wallet_address (primary address)
  portfolioBalance: string;
  cashBalance: string;
  gasSponsored?: boolean;
  smartAccountAddress?: string; // Same as walletAddress when gas sponsored
  magicWalletAddress?: string; // Magic EOA address for signing
  shouldRefreshBalance?: boolean; // Flag to refresh balance after referral signup
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isRefreshingBalances: boolean;
  loginWithProvider: (provider: 'google' | 'twitter' | 'github', referralCode?: string) => Promise<void>;
  login: (referralCode?: string) => Promise<void>;
  loginWithEmail: (email: string, referralCode?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshBalances: () => Promise<void>;
  updateWalletAddress: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshingBalances, setIsRefreshingBalances] = useState(false);
  const [pendingReferralCode, setPendingReferralCode] = useState<string | undefined>(undefined);
  const [authError, setAuthError] = useState<string | null>(null);

  const isAuthenticated = !!user;

  // Auto-dismiss error after 6 seconds
  useEffect(() => {
    if (authError) {
      const timer = setTimeout(() => {
        setAuthError(null);
      }, 6000);
      return () => clearTimeout(timer);
    }
  }, [authError]);

  // Helper function to register user in backend and get user data
  const getUserWithWallet = async (metadata: any, referralCode?: string): Promise<User> => {
    defaultLogger.debug('Magic metadata received', metadata);
    
    // Extract wallet address from either publicAddress or issuer field
    // First, get the Magic wallet address (EOA)
    let magicWalletAddress = metadata.publicAddress;
    if (!magicWalletAddress && metadata.issuer) {
      // For email login, extract address from issuer: "did:ethr:0x..."
      const match = metadata.issuer.match(/did:ethr:(0x[a-fA-F0-9]{40})/);
      magicWalletAddress = match ? match[1] : null;
    }
    
    console.log('Magic wallet address (EOA):', magicWalletAddress);
    
    // IMPORTANT: Get smart account address for gas sponsorship
    // This will be the primary address used for all on-chain operations
    let walletAddress = magicWalletAddress; // Default fallback
    let gasSponsorshipInfo = { isAvailable: false, smartAccountAddress: null as string | null };
    
    try {
      gasSponsorshipInfo = await getGasSponsorshipInfo();
      console.log('🚀 Gas Sponsorship Status:', gasSponsorshipInfo.isAvailable ? 'ENABLED' : 'DISABLED');
      
      if (gasSponsorshipInfo.smartAccountAddress) {
        // Use smart account address as the primary wallet address
        walletAddress = gasSponsorshipInfo.smartAccountAddress;
        console.log('📱 Smart Account (primary):', gasSponsorshipInfo.smartAccountAddress);
        console.log('🔑 Magic Wallet (signer):', magicWalletAddress);
      }
    } catch (error) {
      console.error('❌ Error getting gas sponsorship info:', error);
      // Continue with Magic wallet address as fallback
    }
    
    console.log('Using wallet address for registration:', walletAddress);
    
    try {
      // Register user in backend with both addresses
      const registerBody: any = {
        issuer: metadata.issuer!,
        email: metadata.email!,
        smart_contract_wallet_address: gasSponsorshipInfo.smartAccountAddress || walletAddress, // Smart account address (or Magic wallet as fallback)
        magic_wallet_address: magicWalletAddress, // Magic EOA address
      };
      
      // Include referral code if provided
      console.log('🔍 Referral code for registration:', referralCode);
      if (referralCode) {
        registerBody.referral_code = referralCode;
        defaultLogger.info('Including referral code in registration', { referralCode });
        console.log('✅ Referral code included in registration body');
      } else {
        console.log('❌ No referral code provided for registration');
      }
      
      const registerResponse = await fetch(`${API_URL}/user/register`, {
        method: 'POST',
        credentials: 'include', // Include session cookies
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registerBody),
      });

      if (registerResponse.ok) {
        const registerData = await registerResponse.json();
        defaultLogger.info('User registered in backend');

        // If referral code was provided, log success
        if (referralCode) {
          defaultLogger.info('✅ Registration with referral code successful');
        }
      } else {
        const errorData = await registerResponse.json();
        
        // If referral code is required but missing or invalid, throw error with special flag
        if (registerResponse.status === 400 && errorData.error) {
          const error = new Error(errorData.error) as any;
          error.requiresReferral = errorData.error.includes('Referral code required');
          
          // Log referral code requirement at debug level (expected on startup)
          if (error.requiresReferral) {
            defaultLogger.debug('Referral code required for registration', errorData);
          } else {
            defaultLogger.error('Failed to register user in backend', errorData);
          }
          
          throw error;
        }
        
        defaultLogger.error('Failed to register user in backend', errorData);
      }
      
      // Fetch real wallet balances from smart account address (includes signup bonus if new user)
      // NOTE: walletAddress is now the smart account address (not Magic wallet)
      console.log('📊 Fetching balances for wallet:', walletAddress);
      const balances = await walletService.getWalletBalances(walletAddress, metadata.issuer);

      // Track signup bonus received (frontend best-effort, de-duplicated per issuer)
      try {
        const signupTrackedKey = `signup_bonus_tracked:${metadata.issuer}`;
        const alreadyTracked = localStorage.getItem(signupTrackedKey) === '1';
        // Heuristic: if cash balance is at least 10 (tUSDC) and we haven't tracked yet,
        // emit the event. Backend will also track the authoritative financial event.
        if (!alreadyTracked && typeof balances?.cashBalance === 'number' && balances.cashBalance >= 10) {
          posthog.capture('onboarding:signup_bonus_received', {
            amount: 10,
            currency: 'tUSDC',
            wallet_address: walletAddress, // This is the smart account address
          });
          localStorage.setItem(signupTrackedKey, '1');
        }
      } catch {}
      
      return {
        issuer: metadata.issuer!,
        email: metadata.email!,
        publicAddress: magicWalletAddress || '',
        walletAddress: gasSponsorshipInfo.smartAccountAddress || walletAddress || '', // Primary address (smart account if available)
        portfolioBalance: `$${balances.portfolioBalance.toFixed(2)}`,
        cashBalance: `$${balances.cashBalance.toFixed(2)}`,
        gasSponsored: gasSponsorshipInfo.isAvailable,
        smartAccountAddress: gasSponsorshipInfo.smartAccountAddress || undefined,
        magicWalletAddress: magicWalletAddress,
        shouldRefreshBalance: !!referralCode, // Set flag when referral code was provided
      };
    } catch (error) {
      // If this is a referral code error, re-throw it (don't fallback)
      if ((error as any).requiresReferral || (error instanceof Error && error.message.includes('Referral code'))) {
        // Log at debug level since this is expected on startup
        defaultLogger.debug('Referral code required, cannot load wallet', error);
        throw error;
      }
      
      // For other errors, log at error level
      defaultLogger.error('Failed to fetch wallet balances or register user', error);

      // For other errors (like balance fetching), fallback to $0.00
      // Check gas sponsorship status even in fallback case
      let gasSponsorshipInfo = { isAvailable: false, smartAccountAddress: null as string | null };
      try {
        gasSponsorshipInfo = await getGasSponsorshipInfo();
        defaultLogger.info('🚀 Gas Sponsorship Status (Fallback)', { enabled: !!gasSponsorshipInfo.isAvailable });
      } catch (error) {
        defaultLogger.error('❌ Failed to check gas sponsorship in fallback', error);
      }

      return {
        issuer: metadata.issuer!,
        email: metadata.email!,
        publicAddress: magicWalletAddress || '',
        walletAddress: gasSponsorshipInfo.smartAccountAddress || walletAddress || '', // Primary address (smart account if available)
        portfolioBalance: '$0.00',
        cashBalance: '$0.00',
        gasSponsored: gasSponsorshipInfo.isAvailable,
        smartAccountAddress: gasSponsorshipInfo.smartAccountAddress || undefined,
        magicWalletAddress: magicWalletAddress,
      };
    }
  };

  // Check if user is already logged in on app load
  useEffect(() => {
    const checkUser = async () => {
      try {
        const isLoggedIn = await magic.user.isLoggedIn();
        if (isLoggedIn) {
          const metadata = await magic.user.getInfo();
          const userData = await getUserWithWallet(metadata);
          setUser(userData);
          try {
            posthog.identify(userData.walletAddress || userData.issuer, {
              email: userData.email,
              walletAddress: userData.walletAddress,
            });
          } catch {}
        }
      } catch (error) {
        // Handle referral code requirement silently on startup
        const errorMessage = error instanceof Error ? error.message : String(error);
        if ((error as any).requiresReferral || errorMessage.includes('Referral code')) {
          defaultLogger.debug('User session requires referral code, logging out', error);
          // Silently log out the user
          try {
            await magic.user.logout();
          } catch (logoutError) {
            defaultLogger.debug('Error during silent logout', logoutError);
          }
        } else {
          // For other errors, log as error
          defaultLogger.error('Error checking user authentication', error);
        }
      } finally {
        setIsLoading(false);
      }
    };

    checkUser();
  }, []);

  // Daily balance snapshot trigger
  useEffect(() => {
    const triggerDailySnapshot = async () => {
      if (!user?.issuer || !user?.portfolioBalance || !user?.cashBalance) return;

      try {
        const today = new Date().toISOString().split('T')[0];
        const lastSnapshotKey = `last_balance_snapshot:${user.issuer}`;
        const lastSnapshot = localStorage.getItem(lastSnapshotKey);

        // Only snapshot once per day
        if (lastSnapshot !== today) {
          const portfolioBalance = parseFloat(user.portfolioBalance.replace('$', ''));
          const cashBalance = parseFloat(user.cashBalance.replace('$', ''));

          const response = await fetch(`${API_URL}/user/balance-snapshot`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_issuer: user.issuer,
              portfolio_balance: portfolioBalance,
              cash_balance: cashBalance
            })
          });

          if (response.ok) {
            localStorage.setItem(lastSnapshotKey, today);
            defaultLogger.info('Daily balance snapshot stored successfully');
          }
        }
      } catch (error) {
        defaultLogger.error('Failed to store daily balance snapshot', error);
      }
    };

    // Trigger snapshot after a short delay to ensure user data is fully loaded
    const timeoutId = setTimeout(triggerDailySnapshot, 1000);
    return () => clearTimeout(timeoutId);
  }, [user?.issuer, user?.portfolioBalance, user?.cashBalance]);

  const loginWithEmail = async (email: string, referralCode?: string) => {
    try {
      setIsLoading(true);
      defaultLogger.info('Starting Magic email login for email login');

      // Track login attempt (email)
      try {
        posthog.capture('onboarding:login_attempted', {
          method: 'email',
          referral_code_present: !!referralCode,
          referral_code: referralCode || null,
        });
      } catch {}

      // Store referral code if provided
      if (referralCode) {
        setPendingReferralCode(referralCode);
      }

      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Magic Link request timed out after 30 seconds')), 30000)
      );

      // Use Magic's email-based authentication with timeout
      const didToken = await Promise.race([
        magic.auth.loginWithMagicLink({
          email,
          showUI: true // Show Magic's UI for better user experience
        }),
        timeoutPromise
      ]);

      defaultLogger.info('Email login successful');

      if (didToken) {
        const metadata = await magic.user.getInfo();
        defaultLogger.debug('User metadata from email login', metadata);

        const userData = await getUserWithWallet(metadata, referralCode);
        setUser(userData);
        handleBalanceRefreshAfterSignup(userData);
        try {
          posthog.identify(userData.walletAddress || userData.issuer, {
            email: userData.email,
            walletAddress: userData.walletAddress,
          });
        } catch {}

        // Track login successful (email)
        try {
          posthog.capture('onboarding:login_successful', {
            method: 'email',
            wallet_address: userData.walletAddress,
          });
        } catch {}
        setPendingReferralCode(undefined);
        defaultLogger.info('User set successfully from email login');
      } else {
        throw new Error('No DID token received from Magic Link');
      }
    } catch (error) {
      defaultLogger.error('Email login error', error);

      // Check if this is a referral code requirement error
      const requiresReferral = (error as any).requiresReferral;

      // Logout from Magic if registration failed
      if (requiresReferral) {
        try {
          await magic.user.logout();
        } catch (logoutError) {
          defaultLogger.error('Error logging out after failed registration', logoutError);
        }
      }

      // Provide user-friendly error message
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('Referral code')) {
        setAuthError('Referral code required for signup. Please sign up with a referral code.');
      } else if (errorMessage.includes('timed out')) {
        setAuthError('Magic Link request timed out. Please try again or check your email for the magic link.');
      } else if (errorMessage.includes('cancelled')) {
        defaultLogger.info('User cancelled Magic Link authentication');
      } else {
        setAuthError('Authentication failed. Please try again or use a different email.');
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithProvider = async (provider: 'google' | 'twitter' | 'github', referralCode?: string) => {
    try {
      setIsLoading(true);

      // Clear OAuth processing flag for new login attempt
      sessionStorage.removeItem('oauthProcessed');

      // Store referral code in sessionStorage for OAuth callback
      if (referralCode) {
        sessionStorage.setItem('pendingReferralCode', referralCode);
        defaultLogger.info('Stored referral code for OAuth callback');
      }

      // Track login attempt (provider)
      try {
        posthog.capture('onboarding:login_attempted', {
          method: provider,
          referral_code_present: !!referralCode,
          referral_code: referralCode || null,
        });
      } catch {}

      // Use environment-based redirect URI for Magic SDK v2
      const redirectURI = process.env.REACT_APP_NODE_ENV === 'production' 
        ? 'https://www.theparlaycompany.com/oauth/callback'
        : process.env.REACT_APP_NODE_ENV === 'staging'
        ? 'https://tpc-staging-env-staging-shazam8253-gmailcoms-projects.vercel.app/oauth/callback'  
        : window.location.origin + '/oauth/callback';  // Use current origin for staging/development
      defaultLogger.info('Starting OAuth login', { provider, redirectURI, origin: window.location.origin, protocol: window.location.protocol });

      // Use redirect flow with proper callback handling
      await magic.oauth2.loginWithRedirect({
        provider,
        redirectURI: redirectURI,
      });

      // Note: After redirect, user will be back at /callback
    } catch (error) {
      defaultLogger.error('OAuth login error', error);

      // Check if this is a referral code requirement error
      const requiresReferral = (error as any).requiresReferral;

      // Logout from Magic if registration failed
      if (requiresReferral) {
        try {
          await magic.user.logout();
        } catch (logoutError) {
          console.error('Error logging out after failed registration:', logoutError);
        }
      }

      // Handle referral code errors
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('Referral code')) {
        setAuthError('Referral code required for signup. Please sign up with a referral code.');
      }

      setIsLoading(false);
    }
  };

  // Backward-compatible Google login wrapper
  const login = async (referralCode?: string) => loginWithProvider('google', referralCode);

  const logout = async () => {
    try {
      await magic.user.logout();
      setUser(null);
      try { posthog.reset(); } catch {}
      // Clear OAuth processing flag
      sessionStorage.removeItem('oauthProcessed');
    } catch (error) {
      defaultLogger.error('Logout error', error);
    }
  };

  const refreshBalances = async () => {
    if (!user?.walletAddress) {
      defaultLogger.info('No user or wallet address available for balance refresh');
      return;
    }

    try {
      setIsRefreshingBalances(true);
      defaultLogger.info('Refreshing balances', { wallet: user.walletAddress });
      
      const balances = await walletService.refreshBalances(user.walletAddress, user.issuer);
      
      // Update user with new balance values
      setUser(prevUser => {
        if (!prevUser) return null;
        return {
          ...prevUser,
          portfolioBalance: `$${balances.portfolioBalance.toFixed(2)}`,
          cashBalance: `$${balances.cashBalance.toFixed(2)}`,
        };
      });
      
      defaultLogger.info('Balances refreshed successfully');
    } catch (error) {
      defaultLogger.error('Failed to refresh balances', error);
    } finally {
      setIsRefreshingBalances(false);
    }
  };

  // Helper function to handle balance refresh after referral signup
  const handleBalanceRefreshAfterSignup = (userData: User) => {
    if (userData.shouldRefreshBalance) {
      setTimeout(async () => {
        try {
          await refreshBalances();
          defaultLogger.info('✅ Balance refreshed after referral signup');
        } catch (error) {
          defaultLogger.error('Failed to refresh balance after referral signup', error);
        }
      }, 2000); // 2 second delay to ensure backend processing is complete
    }
  };

  // Function to update wallet address for existing logged-in users
  const updateWalletAddress = async () => {
    if (!user?.issuer) {
      defaultLogger.info('No user issuer available for wallet update');
      return;
    }

    try {
      // Check gas sponsorship status
      const gasSponsorshipInfo = await getGasSponsorshipInfo();
      
      if (gasSponsorshipInfo.isAvailable && gasSponsorshipInfo.smartAccountAddress) {
        defaultLogger.info('🔄 Updating wallet address to smart account', { address: gasSponsorshipInfo.smartAccountAddress });
        
        const updateResponse = await fetch(`${API_URL}/user/update-wallet`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            issuer: user.issuer,
            smart_contract_wallet_address: gasSponsorshipInfo.smartAccountAddress,
            magic_wallet_address: user.magicWalletAddress || user.publicAddress
          })
        });
        
        if (updateResponse.ok) {
          defaultLogger.info('✅ Updated user wallet address to smart account');
          
          // Update the user state with new wallet address
          setUser(prevUser => {
            if (!prevUser) return null;
            return {
              ...prevUser,
              walletAddress: gasSponsorshipInfo.smartAccountAddress || prevUser.walletAddress,
              smartAccountAddress: gasSponsorshipInfo.smartAccountAddress || undefined,
              gasSponsored: gasSponsorshipInfo.isAvailable,
              magicWalletAddress: prevUser.magicWalletAddress || prevUser.publicAddress
            };
          });
        } else {
          defaultLogger.warn('⚠️ Failed to update wallet address in backend');
        }
      }
    } catch (error) {
      defaultLogger.error('❌ Error updating wallet address', error);
    }
  };

  // Handle OAuth callback when on /oauth/callback route
  React.useEffect(() => {
    if (window.location.pathname === '/oauth/callback') {
      const handleOAuthResult = async () => {
        // Check if we've already processed this OAuth callback
        const oauthProcessed = sessionStorage.getItem('oauthProcessed');
        if (oauthProcessed) {
          console.log('🔍 OAuth already processed, skipping...');
          return;
        }
        
        try {
          defaultLogger.info('Handling OAuth redirect result', { url: window.location.href });
          sessionStorage.setItem('oauthProcessed', 'true');
          
          // Retrieve referral code from sessionStorage if present
          const storedReferralCode = sessionStorage.getItem('pendingReferralCode');
          console.log('🔍 Stored referral code:', storedReferralCode);
          if (storedReferralCode) {
            defaultLogger.info('Retrieved referral code from sessionStorage', { referralCode: storedReferralCode });
          } else {
            console.log('⚠️ No referral code found in sessionStorage');
          }
          
          // First check if user is already logged in
          const isLoggedIn = await magic.user.isLoggedIn();
          defaultLogger.info('User already logged in', { isLoggedIn });
          
          if (isLoggedIn) {
            // User is already logged in, get their info
            const metadata = await magic.user.getInfo();
            defaultLogger.debug('User metadata from existing session', metadata);
            
            const userData = await getUserWithWallet(metadata, storedReferralCode || undefined);
            setUser(userData);
            handleBalanceRefreshAfterSignup(userData);
            try {
              posthog.identify(userData.walletAddress || userData.issuer, {
                email: userData.email,
                walletAddress: userData.walletAddress,
              });
            } catch {}
            defaultLogger.info('User set from existing session');
            
            // Clear stored referral code
            if (storedReferralCode) {
              sessionStorage.removeItem('pendingReferralCode');
            }
            return;
          }
          
          // Try to get redirect result
          console.log('🔍 Attempting to get OAuth redirect result...');
          const result = await magic.oauth2.getRedirectResult({});
          console.log('🔍 OAuth redirect result:', result);
          defaultLogger.info('OAuth redirect result received', { result });
          
          if (result) {
            defaultLogger.info('Getting user info after redirect');
            const metadata = await magic.user.getInfo();
            defaultLogger.debug('User metadata after redirect', metadata);
            
            const userData = await getUserWithWallet(metadata, storedReferralCode || undefined);
            setUser(userData);
            handleBalanceRefreshAfterSignup(userData);
            try {
              posthog.identify(userData.walletAddress || userData.issuer, {
                email: userData.email,
                walletAddress: userData.walletAddress,
              });
            } catch {}
            // Track login successful with provider-aware method if available
            try {
              const providerForAnalytics = (result as any)?.oauth?.provider || (result as any)?.provider || 'unknown';
              posthog.capture('onboarding:login_successful', {
                method: providerForAnalytics,
                wallet_address: userData.walletAddress,
              });
            } catch {}
            defaultLogger.info('User set successfully from redirect');
            
            // Clear stored referral code
            if (storedReferralCode) {
              sessionStorage.removeItem('pendingReferralCode');
            }
          } else {
            console.log('❌ No OAuth result found and user not logged in');
            defaultLogger.info('No OAuth result found and user not logged in');
          }
        } catch (error) {
          console.log('❌ OAuth redirect error:', error);
          defaultLogger.error('OAuth redirect error', error);

          // Check if this is a referral code requirement error
          const requiresReferral = (error as any).requiresReferral;

          // Logout from Magic if registration failed due to referral requirement
          if (requiresReferral) {
            try {
              await magic.user.logout();
              defaultLogger.info('Logged out from Magic after referral code error');
            } catch (logoutError) {
              defaultLogger.error('Error logging out after failed registration', logoutError);
            }
          }

          // Show user-friendly error message
          const errorMessage = error instanceof Error ? error.message : String(error);
          if (errorMessage.includes('Referral code')) {
            // Set flags for App.tsx to auto-open signup modal with referral code input
            sessionStorage.setItem('isSignup', 'true');
            sessionStorage.setItem('needsReferralSignup', 'true');
            // Clear oauthProcessed flag so next attempt can process
            sessionStorage.removeItem('oauthProcessed');
            console.log('✅ Set sessionStorage flags:', {
              isSignup: sessionStorage.getItem('isSignup'),
              needsReferralSignup: sessionStorage.getItem('needsReferralSignup'),
              oauthProcessed: sessionStorage.getItem('oauthProcessed')
            });
            defaultLogger.info('Redirecting to signup - referral code required for new user');
            // Redirect to home page to open signup modal
            console.log('🔄 Redirecting to home page...');
            window.location.href = '/';
          } else {
            setAuthError('Authentication failed. Please try again.');
          }
        } finally {
          setIsLoading(false);
        }
      };
      
      handleOAuthResult();
    }
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    isRefreshingBalances,
    loginWithProvider,
    login,
    loginWithEmail,
    logout,
    refreshBalances,
    updateWalletAddress,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {authError && (
        <div style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10000,
          maxWidth: '500px',
          width: '90%'
        }}>
          <div style={{
            padding: '16px 20px',
            backgroundColor: '#FEF2F2',
            color: '#DC2626',
            border: '1px solid #FCA5A5',
            borderRadius: '8px',
            fontSize: '15px',
            lineHeight: '1.5',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span>{authError}</span>
            <button
              onClick={() => setAuthError(null)}
              style={{
                background: 'none',
                border: 'none',
                color: '#DC2626',
                fontSize: '20px',
                cursor: 'pointer',
                padding: '0 4px',
                lineHeight: '1',
                flexShrink: 0
              }}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};